import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../app/AppContext";
import { ApiError } from "./api-client";
import type { BillingChangeStatus } from "./types";

/**
 * Billing data hooks (2026-07-28 commerce cutover).
 *
 * ONE snapshot query key for every billing consumer — the old
 * ["billing"] vs ["billing-summary"] split meant AccountMenu and ProfilePage
 * could disagree after a plan change. Everything billing renders comes from
 * GET /billing/account; checkout/portal return query params never decide what
 * renders, they only start the convergence poll below, which reads the returning
 * selection's own status (billing.selection).
 */

export const BILLING_ACCOUNT_QUERY_KEY = ["billing-account"] as const;

export function useBillingAccount(options: { enabled?: boolean } = {}) {
  const { api } = useAppContext();
  return useQuery({
    queryKey: BILLING_ACCOUNT_QUERY_KEY,
    queryFn: () => api.getBillingAccount(),
    staleTime: 60_000,
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

export function useBillingCatalog() {
  const { api, locale } = useAppContext();
  return useQuery({
    queryKey: ["billing-catalog", locale],
    queryFn: () => api.getBillingCatalog(locale),
    staleTime: 300_000,
  });
}

// --- Checkout/Portal return convergence -------------------------------------

/** The statuses a selection ends on; pending, processing and scheduled keep the poll going. */
const SETTLED_STATUSES = ["applied", "failed", "canceled", "expired", "superseded"] as const;
export type SettledSelectionStatus = (typeof SETTLED_STATUSES)[number];

export type SelectionResolution = { done: false } | { done: true; status: SettledSelectionStatus };

/**
 * Whether the SPECIFIC selection the return URL correlates has ended, from its own
 * status as billing.selection reads it. An already-active subscription is
 * deliberately NOT convergence, and nothing in the account snapshot is: the
 * snapshot names only the open selection and the last applied one, never a
 * selection that failed, expired, was canceled or superseded.
 */
export function resolveSelection(status: BillingChangeStatus): SelectionResolution {
  const settled = SETTLED_STATUSES.find((candidate) => candidate === status);
  return settled ? { done: true, status: settled } : { done: false };
}

/**
 * The server knows no such selection for this viewer: 404 selection_not_found
 * (another account's id, or one that names nothing) or 400 for a value that is no
 * selection id. Distinct from any outcome — the selection may exist elsewhere.
 */
function isUnknownSelection(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 400);
}

export const billingSelectionQueryKey = (selectionId: string) =>
  ["billing-selection", selectionId] as const;

/** Poll cadence: every 2s for 30s, then every 10s up to 120s, then stop. */
export function pollDelay(elapsedMs: number): number | null {
  if (elapsedMs >= 120_000) return null;
  return elapsedMs < 30_000 ? 2_000 : 10_000;
}

export type CheckoutReturnState = {
  /**
   * - idle: no return params — nothing to show.
   * - cancelled: the user backed out of checkout (?checkout=cancelled).
   * - processing: polling the correlated selection's status.
   * - still-processing: past the 120s cap; manual refresh only. Not an outcome.
   * - resolved: the selection ended (`resolvedStatus`); the snapshot is re-read.
   * - unknown: the server knows no such selection for this viewer. Not an outcome.
   */
  phase: "idle" | "cancelled" | "processing" | "still-processing" | "resolved" | "unknown";
  resolvedStatus: SettledSelectionStatus | null;
  refresh: () => void;
};

/**
 * Reads ?checkout=success|cancelled, ?portal=return and the opaque
 * ?selection=<id> correlation param, and polls THAT selection's own status
 * (billing.selection — a read; the purchase is never repeated) until it ends on
 * applied | failed | canceled | expired | superseded. A failed lookup keeps the
 * poll going; past the cap the state is still-processing, never an outcome. The
 * params grant nothing: plan and access always render from the snapshot.
 */
export function useCheckoutReturnPoll(): CheckoutReturnState {
  const { api } = useAppContext();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const checkoutParam = searchParams.get("checkout");
  const portalReturn = searchParams.get("portal") === "return";
  const selectionParam = searchParams.get("selection");

  const cancelled = checkoutParam === "cancelled";
  const pollingSelectionId =
    !cancelled && (checkoutParam === "success" || portalReturn) ? selectionParam : null;
  const selectionQuery = useQuery({
    queryKey: billingSelectionQueryKey(pollingSelectionId ?? ""),
    queryFn: () => api.getBillingSelection(pollingSelectionId ?? ""),
    enabled: pollingSelectionId !== null,
    // The poll below is the retry; a 404 is an answer, not a blip.
    retry: false,
  });
  const resolution = selectionQuery.data ? resolveSelection(selectionQuery.data.status) : null;
  const converged = resolution?.done === true;
  const unknown =
    pollingSelectionId !== null &&
    selectionQuery.data === undefined &&
    isUnknownSelection(selectionQuery.error);
  const ended = converged || unknown;
  const [capped, setCapped] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: BILLING_ACCOUNT_QUERY_KEY });
    if (pollingSelectionId)
      void queryClient.invalidateQueries({
        queryKey: billingSelectionQueryKey(pollingSelectionId),
      });
  };

  // A plain portal return (no selection to converge on) still refreshes the
  // snapshot once — the user may have fixed a payment method or cancelled.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh once per portal arrival
  useEffect(() => {
    if (portalReturn && !selectionParam) refresh();
  }, [portalReturn, selectionParam]);

  // The selection ended: the snapshot is read once more, so plan and access show it.
  useEffect(() => {
    if (converged) void queryClient.invalidateQueries({ queryKey: BILLING_ACCOUNT_QUERY_KEY });
  }, [converged, queryClient]);

  useEffect(() => {
    if (!pollingSelectionId || ended) return;
    const startedAt = Date.now();
    let timer: number | undefined;
    let disposed = false;
    const schedule = () => {
      const delay = pollDelay(Date.now() - startedAt);
      if (delay === null) {
        setCapped(true);
        return;
      }
      timer = window.setTimeout(() => {
        void queryClient
          .invalidateQueries({ queryKey: billingSelectionQueryKey(pollingSelectionId) })
          .finally(() => {
            if (!disposed) schedule();
          });
      }, delay);
    };
    setCapped(false);
    schedule();
    return () => {
      disposed = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [pollingSelectionId, ended, queryClient]);

  if (cancelled) return { phase: "cancelled", resolvedStatus: null, refresh };
  if (!pollingSelectionId) return { phase: "idle", resolvedStatus: null, refresh };
  if (resolution?.done) return { phase: "resolved", resolvedStatus: resolution.status, refresh };
  if (unknown) return { phase: "unknown", resolvedStatus: null, refresh };
  if (capped) return { phase: "still-processing", resolvedStatus: null, refresh };
  return { phase: "processing", resolvedStatus: null, refresh };
}
